import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { CheckCircle2, Inbox, LifeBuoy, Reply } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Label } from "@/components/ui/text-field";

const TEXTAREA_CLASS =
  "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@famidex/shared";
import { useI18n } from "@/providers/i18n-provider";
import { showToast } from "@/components/ui/toast";

type ErrorModel = components["schemas"]["ErrorModel"];
type SupportTicketListResponse = components["schemas"]["SupportTicketListResponse"];
type SupportTicket = components["schemas"]["SupportTicket"];

type StatusFilter = "all" | "open" | "in_progress" | "closed";
const FILTERS: StatusFilter[] = ["all", "open", "in_progress", "closed"];

const priorityClass = (p: string) =>
  p === "high"
    ? "bg-destructive/10 text-destructive ring-destructive/20"
    : p === "low"
      ? "bg-muted text-muted-foreground ring-border"
      : "bg-warning/15 text-warning ring-warning/20";

/**
 * Tickets de soporte del back-office. Lista todos los tickets (aislados por
 * usuario en origen, agregados aquí), permite responder y cerrar. Gated en el
 * servidor por `ManageContent`. Alta la hacen los usuarios desde `/support`.
 */
export function SupportTickets() {
  const { t } = useI18n();
  const [filter, setFilter] = createSignal<StatusFilter>("open");
  const [replyingId, setReplyingId] = createSignal<string | null>(null);
  const [replyText, setReplyText] = createSignal("");

  const query = createApiQuery<SupportTicketListResponse, ErrorModel>(
    () => ["admin", "support", "tickets", filter()],
    async () =>
      api.GET("/api/admin/support/tickets", {
        params: { query: filter() === "all" ? {} : { status: filter() } },
      }),
    () => ({ staleTime: 10_000 }),
  );

  const updateMutation = createApiMutation(
    async (vars: { id: string; reply?: string; status?: "open" | "in_progress" | "closed" }) =>
      api.POST("/api/admin/support/tickets/{id}", {
        params: { path: { id: vars.id } },
        body: { reply: vars.reply ?? null, status: vars.status ?? null, priority: null },
      }),
  );

  const sendReply = async (ticket: SupportTicket) => {
    const reply = replyText().trim();
    if (!reply) {
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: ticket.id, reply, status: "closed" });
      showToast(t(msg`Reply sent and ticket closed.`), "success");
      setReplyingId(null);
      setReplyText("");
      await query.refetch();
    } catch {
      showToast(t(msg`Couldn't update the ticket.`), "error");
    }
  };

  const setStatus = async (ticket: SupportTicket, status: "open" | "in_progress" | "closed") => {
    try {
      await updateMutation.mutateAsync({ id: ticket.id, status });
      await query.refetch();
    } catch {
      showToast(t(msg`Couldn't update the ticket.`), "error");
    }
  };

  const filterLabel = (f: StatusFilter) =>
    f === "all"
      ? t(msg`All`)
      : f === "open"
        ? t(msg`Open`)
        : f === "in_progress"
          ? t(msg`In progress`)
          : t(msg`Closed`);

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <span class="grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent ring-1 ring-accent/20">
          <LifeBuoy class="h-6 w-6" />
        </span>
        <div>
          <h2 class="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {t(msg`Support Tickets`)}
          </h2>
          <p class="text-sm text-muted-foreground">{t(msg`Nurture every collector inquiry.`)}</p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2" role="tablist" aria-label={t(msg`Filter tickets`)}>
        <For each={FILTERS}>
          {(f) => (
            <button
              type="button"
              role="tab"
              aria-selected={filter() === f}
              onClick={() => setFilter(f)}
              class="rounded-full px-4 py-1.5 text-sm font-medium ring-1 transition-colors"
              classList={{
                "bg-accent text-accent-foreground ring-accent": filter() === f,
                "bg-card text-muted-foreground ring-border hover:text-foreground": filter() !== f,
              }}
            >
              {filterLabel(f)}
            </button>
          )}
        </For>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-4">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-32 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={(query.data?.data.length ?? 0) > 0}
          fallback={
            <EmptyState
              icon={Inbox}
              title={t(msg`No tickets here`)}
              description={t(msg`When collectors reach out, their tickets land here.`)}
            />
          }
        >
          <div class="space-y-4">
            <For each={query.data?.data ?? []}>
              {(ticket) => (
                <Card>
                  <CardContent class="space-y-3 p-5">
                    <div class="flex flex-wrap items-start justify-between gap-3">
                      <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                          <h3 class="font-semibold text-foreground">{ticket.subject}</h3>
                          <span
                            class={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${priorityClass(ticket.priority)}`}
                          >
                            {ticket.priority}
                          </span>
                          <span class="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent ring-1 ring-accent/20">
                            {ticket.category}
                          </span>
                        </div>
                        <p class="mt-1 text-xs text-muted-foreground">
                          <span class="font-medium text-accent">{ticket.userId}</span>
                          {" · "}
                          <span class="font-mono">{ticket.id}</span>
                        </p>
                      </div>
                      <span
                        class="inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ring-1"
                        classList={{
                          "bg-warning/15 text-warning ring-warning/20": ticket.status === "open",
                          "bg-accent/15 text-accent ring-accent/20":
                            ticket.status === "in_progress",
                          "bg-muted text-muted-foreground ring-border": ticket.status === "closed",
                        }}
                      >
                        <Show when={ticket.status === "closed"}>
                          <CheckCircle2 class="h-3.5 w-3.5" />
                        </Show>
                        {ticket.status === "in_progress"
                          ? t(msg`In progress`)
                          : ticket.status === "closed"
                            ? t(msg`Closed`)
                            : t(msg`Open`)}
                      </span>
                    </div>

                    <p class="rounded-lg border border-dashed border-border bg-muted/40 p-3 text-sm text-foreground">
                      {ticket.body}
                    </p>

                    <Show when={ticket.reply}>
                      <p class="rounded-lg bg-accent/5 p-3 text-sm text-foreground ring-1 ring-accent/10">
                        <span class="font-medium text-accent">{t(msg`Reply`)}: </span>
                        {ticket.reply}
                      </p>
                    </Show>

                    <Show
                      when={replyingId() === ticket.id}
                      fallback={
                        <div class="flex flex-wrap gap-2">
                          <Show when={ticket.status !== "closed"}>
                            <Button
                              size="sm"
                              onClick={() => {
                                setReplyingId(ticket.id);
                                setReplyText(ticket.reply ?? "");
                              }}
                            >
                              <Reply class="mr-1 h-4 w-4" />
                              {t(msg`Reply`)}
                            </Button>
                          </Show>
                          <Show when={ticket.status === "open"}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setStatus(ticket, "in_progress")}
                            >
                              {t(msg`Mark in progress`)}
                            </Button>
                          </Show>
                          <Show when={ticket.status === "closed"}>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setStatus(ticket, "open")}
                            >
                              {t(msg`Reopen`)}
                            </Button>
                          </Show>
                        </div>
                      }
                    >
                      <div class="space-y-2">
                        <Label for={`reply-${ticket.id}`}>{t(msg`Your reply`)}</Label>
                        <textarea
                          id={`reply-${ticket.id}`}
                          rows={3}
                          class={TEXTAREA_CLASS}
                          value={replyText()}
                          onInput={(e) => setReplyText(e.currentTarget.value)}
                          placeholder={t(msg`Write a warm, helpful reply…`)}
                        />
                        <div class="flex gap-2">
                          <Button
                            size="sm"
                            loading={updateMutation.isPending}
                            disabled={!replyText().trim()}
                            onClick={() => sendReply(ticket)}
                          >
                            {t(msg`Send & close`)}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setReplyingId(null);
                              setReplyText("");
                            }}
                          >
                            {t(msg`Cancel`)}
                          </Button>
                        </div>
                      </div>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
